import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AgentService } from '../../services/agent.service';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';

interface BottomNavItem {
  icon: SafeHtml;
  label: string;
  route: string;
}

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  template: `
    <nav class="xl:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 safe-area-bottom">
      <div class="flex justify-around items-stretch h-16" *ngIf="navMode$ | async as mode">
        <ng-container *ngIf="mode === 'agent'">
          <a *ngFor="let item of agentNavItems"
             [routerLink]="item.route"
             class="flex flex-col items-center justify-center flex-1 min-w-0 py-1 transition-colors duration-200"
             [class.text-purple-600]="isRouteActive(item.route)"
             [class.text-gray-400]="!isRouteActive(item.route)">
            <div class="w-6 h-6 mb-0.5" [innerHTML]="item.icon"></div>
            <span class="text-[10px] font-medium truncate max-w-full px-1">{{ item.label | translate }}</span>
            <div *ngIf="isRouteActive(item.route)" class="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full gradient-primary"></div>
          </a>
        </ng-container>
        <ng-container *ngIf="mode === 'admin'">
          <a *ngFor="let item of adminNavItems"
             [routerLink]="item.route"
             class="flex flex-col items-center justify-center flex-1 min-w-0 py-1 transition-colors duration-200"
             [class.text-purple-600]="isRouteActive(item.route)"
             [class.text-gray-400]="!isRouteActive(item.route)">
            <div class="w-6 h-6 mb-0.5" [innerHTML]="item.icon"></div>
            <span class="text-[10px] font-medium truncate max-w-full px-1">{{ item.label | translate }}</span>
            <div *ngIf="isRouteActive(item.route)" class="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full gradient-primary"></div>
          </a>
        </ng-container>

        <!-- Toggle button: only for dual-access users -->
        <button *ngIf="hasDualAccess$ | async"
                (click)="toggleNavMode()"
                class="flex flex-col items-center justify-center flex-1 min-w-0 py-1 transition-colors duration-200 text-purple-600">
          <div class="w-7 h-7 mb-0.5 rounded-full gradient-primary flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
            </svg>
          </div>
          <span class="text-[10px] font-medium truncate max-w-full px-1">
            {{ mode === 'agent' ? ('BOTTOM_NAV.SWITCH_ADMIN' | translate) : ('BOTTOM_NAV.SWITCH_AGENT' | translate) }}
          </span>
        </button>
      </div>
    </nav>
  `,
  styles: [`
    :host {
      display: block;
    }
    a, button {
      position: relative;
      -webkit-tap-highlight-color: transparent;
    }
  `]
})
export class BottomNavComponent implements OnInit {
  navMode$!: Observable<'agent' | 'admin'>;
  hasDualAccess$!: Observable<boolean>;
  private toggleMode$ = new BehaviorSubject<'agent' | 'admin'>('agent');

  agentNavItems: BottomNavItem[] = [
    {
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>',
      label: 'BOTTOM_NAV.ORDERS_TODAY',
      route: '/agent-dashboard'
    },
    {
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>',
      label: 'BOTTOM_NAV.CALL',
      route: '/order'
    },
    /*{
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>',
      label: 'BOTTOM_NAV.UPCOMING',
      route: '/agent-upcoming-orders'
    },*/
    {
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>',
      label: 'BOTTOM_NAV.SHOP',
      route: '/shop-orders'
    },
   /* {
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>',
      label: 'BOTTOM_NAV.CALCULATOR',
      route: '/agent-delivery-calculator'
    }*/
  ];

  adminNavItems: BottomNavItem[] = [
    {
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 1v6m8-6v6M3 7h18"></path></svg>',
      label: 'BOTTOM_NAV.DASHBOARD',
      route: '/dashboard'
    },
    {
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>',
      label: 'BOTTOM_NAV.ONLINE_DASHBOARD',
      route: '/online-dashboard'
    },
    {
      icon: '<svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>',
      label: 'BOTTOM_NAV.ADMIN_ORDERS',
      route: '/admin-orders'
    }
  ];

  constructor(
    private agentService: AgentService,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
    this.agentNavItems = this.agentNavItems.map(item => ({
      ...item,
      icon: this.sanitizer.bypassSecurityTrustHtml(item.icon as string)
    }));
    this.adminNavItems = this.adminNavItems.map(item => ({
      ...item,
      icon: this.sanitizer.bypassSecurityTrustHtml(item.icon as string)
    }));
  }

  ngOnInit() {
    const isAgent$ = this.agentService.isAgent();
    const isAdmin$ = this.agentService.isAdmin();

    this.hasDualAccess$ = isAdmin$;

    this.navMode$ = combineLatest([isAgent$, isAdmin$, this.toggleMode$]).pipe(
      map(([isAgent, isAdmin, toggleMode]) => {
        if (isAgent && !isAdmin) return 'agent' as const;
        if (isAdmin) return toggleMode;
        return 'agent' as const;
      })
    );
  }

  toggleNavMode(): void {
    const current = this.toggleMode$.value;
    this.toggleMode$.next(current === 'agent' ? 'admin' : 'agent');
  }

  isRouteActive(route: string): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return currentUrl === route;
  }
}
